// src/trimble/coreFiles.ts

// Discover regional Core API origin at runtime. In the browser, only the
// app.connect Trimble regions endpoint is currently usable without noisy
// DNS/CORS/redirect failures from the older fallback origins.
const DEFAULT_MASTER_REGION_ENDPOINTS = [
  "https://app.connect.trimble.com/tc/api/2.0/regions",
];
const coreOriginCache = new Map<string, Promise<string>>();

const DEFAULT_BACKEND_BASE =
  "https://niceconnect-itwin-api-a0htfdavf5a0drbb.centralus-01.azurewebsites.net";
const BACKEND_BASE_STORAGE_KEY = "tc.backendBase";
const BACKEND_BASE_DISABLED_SENTINEL = "__DISABLED__";

function normalizeBackendBase(value?: string | null): string | null {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return null;
  if (/^(off|none|disabled|direct)$/i.test(trimmed)) {
    return BACKEND_BASE_DISABLED_SENTINEL;
  }
  return trimmed.replace(/\/+$/, "");
}

function readBackendBaseQueryParam(): string | null {
  try {
    const url = new URL(window.location.href);
    const fromQuery = normalizeBackendBase(url.searchParams.get("tcBackendBase"));
    if (fromQuery) {
      window.sessionStorage.setItem(BACKEND_BASE_STORAGE_KEY, fromQuery);
      return fromQuery;
    }
  } catch {
    // ignore
  }

  try {
    return normalizeBackendBase(
      window.sessionStorage.getItem(BACKEND_BASE_STORAGE_KEY)
    );
  } catch {
    return null;
  }
}

export function getBackendBase(): string | null {
  const fromQueryOrSession = readBackendBaseQueryParam();
  if (fromQueryOrSession === BACKEND_BASE_DISABLED_SENTINEL) {
    return null;
  }

  const fromEnv = normalizeBackendBase(process.env.REACT_APP_TRIMBLE_BACKEND_BASE);
  if (fromEnv === BACKEND_BASE_DISABLED_SENTINEL) {
    return null;
  }

  return fromQueryOrSession ?? fromEnv ?? DEFAULT_BACKEND_BASE;
}

function getMasterRegionEndpoints(): string[] {
  try {
    const override = window?.localStorage?.getItem("tc.masterRegionEndpoint");
    if (override && override.trim().length > 0) return [override.trim()];
  } catch {
    // ignore storage errors
  }

  return [...DEFAULT_MASTER_REGION_ENDPOINTS];
}

function normalizeOrigin(origin: string): string {
  const trimmed = origin.trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
  return `https://${trimmed}`;
}

function normalizeRegionOrigins(payload: any): string[] {
  const list = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.regions)
    ? payload.regions
    : [];

  const origins = list
    .map((r: any) => (typeof r?.origin === "string" ? normalizeOrigin(r.origin) : ""))
    .filter((v: string) => v.length > 0);

  // Deduplicate while preserving order.
  return Array.from(new Set(origins));
}

async function fetchRegionOrigins(): Promise<string[]> {
  const masterEndpoints = getMasterRegionEndpoints();
  let lastError: Error | null = null;

  for (const endpoint of masterEndpoints) {
    try {
      const res = await fetch(endpoint);
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        lastError = new Error(`Regions discovery failed (${res.status}): ${text}`);
        continue;
      }

      const json = await res.json().catch(() => ({}));
      const origins = normalizeRegionOrigins(json);
      if (origins.length === 0) {
        lastError = new Error("Regions discovery returned no origins.");
        continue;
      }

      console.info("[Trimble] Regions discovered via:", endpoint, origins);
      return origins;
    } catch (err) {
      lastError = err as Error;
    }
  }

  throw lastError ?? new Error("Regions discovery failed for all candidates.");
}

async function resolveCoreOrigin(
  accessToken?: string,
  projectId?: string
): Promise<string> {
  const cacheKey = projectId ? `project:${projectId}` : "default";
  const existing = coreOriginCache.get(cacheKey);
  if (existing) return existing;

  const promise = (async () => {
    const origins = await fetchRegionOrigins();

    if (projectId && accessToken) {
      let sawAuthError = false;

      for (const origin of origins) {
        const res = await fetch(
          `${origin}/tc/api/2.0/projects/${encodeURIComponent(projectId)}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              Accept: "application/json",
            },
          }
        );

        if (res.ok) {
          console.info("[Trimble] Resolved Core API origin:", origin, cacheKey);
          return origin;
        }
        if (res.status === 401 || res.status === 403) sawAuthError = true;
      }

      if (sawAuthError) {
        throw new Error("Access token rejected while resolving project region.");
      }
    }

    // Fallback to first origin if we could not resolve by project id.
    console.info("[Trimble] Using fallback Core API origin:", origins[0], cacheKey);
    return origins[0];
  })();

  coreOriginCache.set(cacheKey, promise);
  return promise;
}

/**
 * Trimble Connect "item" as returned by folder listing.
 * Type field naming varies; we normalize downstream.
 */
export type TcFolderItem = {
  id: string;
  name: string;
  type?: string; // "FOLDER" | "FILE" etc (not always consistent)
};

// ------------
// Folder listing
// ------------

function normalizeItems(payload: any): any[] {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function normalizeType(obj: any): string {
  const t = obj?.type ?? obj?.objectType ?? obj?.resourceType ?? obj?.itemType;
  return String(t ?? "").toUpperCase();
}

/**
 * Lists immediate children of a folder.
 * NOTE: This endpoint shape can vary slightly by deployment; we normalize response.
 */
export async function listFolderItems(
  accessToken: string,
  folderId: string,
  projectId?: string
): Promise<TcFolderItem[]> {
  const coreOrigin = await resolveCoreOrigin(accessToken, projectId);
  const res = await fetch(
    `${coreOrigin}/tc/api/2.0/folders/${encodeURIComponent(folderId)}/items`,
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    }
  );

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`List folder items failed (${res.status}): ${text}`);
  }

  const json = await res.json().catch(() => ({}));
  const raw = normalizeItems(json);

  return raw.map((r: any) => ({
    id: String(r.id),
    name: String(r.name ?? r.title ?? "Unnamed"),
    type: normalizeType(r) || undefined,
  }));
}

// ------------
// Upload
// ------------

export type UploadFileToFolderArgs = {
  accessToken: string;
  projectId?: string; // optional; some endpoints don't require it. Keep for future.
  folderId: string;
  blob: Blob;
  filename: string;
};

/**
 * Uploads a file blob into a TC folder.
 * This matches your callsite: uploadFileToFolder({ accessToken, folderId, blob, filename, ... })
 */
export async function uploadFileToFolder(args: UploadFileToFolderArgs): Promise<any> {
  const { accessToken, folderId, blob, filename, projectId } = args;

  const form = new FormData();
  form.append("file", blob, filename);
  form.append("folderId", folderId);
  if (projectId) form.append("projectId", projectId);
  if (filename) form.append("filename", filename);

  const backendBase = getBackendBase();
  console.info("[Trimble] Backend base:", backendBase ?? "(none)");
  if (backendBase) {
    const res = await fetch(`${backendBase.replace(/\/$/, "")}/trimble/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
        body: form,
    });

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`Upload failed (${res.status}): ${text}`);
    }

    return res.json().catch(() => ({}));
  }

  // Most common Core upload pattern: POST /files?parentId=<folderId>
  const coreOrigin = await resolveCoreOrigin(accessToken, projectId);
  const res = await fetch(
    `${coreOrigin}/tc/api/2.0/files?parentId=${encodeURIComponent(folderId)}`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${accessToken}` },
      body: form,
    }
  );

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Upload failed (${res.status}): ${text}`);
  }

  return res.json().catch(() => ({}));
}

/**
 * Optional legacy wrapper if you still want the older positional signature elsewhere.
 */
export async function uploadFileToFolderLegacy(
  accessToken: string,
  parentFolderId: string,
  blob: Blob,
  filename: string
): Promise<any> {
  return uploadFileToFolder({ accessToken, folderId: parentFolderId, blob, filename });
}

export async function getProjectRootFolderId(
  projectId: string,
  accessToken: string
): Promise<string> {
  const coreOrigin = await resolveCoreOrigin(accessToken, projectId);
  const res = await fetch(`${coreOrigin}/tc/api/2.0/projects/${encodeURIComponent(projectId)}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: "application/json",
    },
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Get project failed (${res.status}): ${text}`);
  }

  const json: any = await res.json();
  if (!json?.rootId) throw new Error("Project response missing rootId");
  return String(json.rootId);
}


